/**
 * A chat reads the bootstrap rendered into its container once. A chat that is destroyed and shown again in the
 * same page view, such as a Coach on a path step the user comes back to, fetches its thread again instead, so it
 * continues from where the thread is now and not from where it was at page load.
 */
export type ChatbotRestartSource = {
    bootstrapUrl: string;
    runMetadata: Record<string, unknown>;
};

const restartSources = new WeakMap<HTMLElement, ChatbotRestartSource>();

export const rememberChatbotRestartSource = (container: HTMLElement, source: ChatbotRestartSource): void => {
    if (source.bootstrapUrl === '') {
        restartSources.delete(container);
        return;
    }

    restartSources.set(container, {...source});
};

/**
 * Fetches a fresh bootstrap for a container whose chat was shown before. Null when it never was.
 */
export const fetchChatbotRestartBootstrap = async (
    container: HTMLElement,
    headers: Record<string, string>,
): Promise<Record<string, unknown> | null> => {
    const source = restartSources.get(container);
    if (!source) {
        return null;
    }

    return fetchChatbotBootstrap(source.bootstrapUrl, source.runMetadata, headers);
};

export const fetchChatbotBootstrap = async (
    bootstrapUrl: string,
    runMetadata: Record<string, unknown>,
    headers: Record<string, string>,
): Promise<Record<string, unknown>> => {
    const response = await fetch(bootstrapUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            ...headers,
        },
        body: JSON.stringify({forwardedProps: runMetadata}),
    });
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }

    const payload: unknown = await response.json();
    if (!isRecord(payload) || !isRecord(payload.chatbot)) {
        throw new Error('Unexpected chatbot bootstrap response.');
    }

    return payload.chatbot;
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
    typeof value === 'object' && value !== null && !Array.isArray(value)
);
